import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  Remove as RemoveIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import {
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

interface Provider {
  id: string;
  name: string;
  description: string;
  icon: string;
  fields: Array<{
    key: string;
    label: string;
    type: string;
    required: boolean;
    help?: string;
  }>;
}

interface Integration {
  id?: string;
  name: string;
  provider: string;
  is_active: boolean;
  webhook_url?: string;
  webhook_secret?: string;
  config?: {
    products?: Array<{
      name: string;
      stage_order: number;
    }>;
    approved_events?: Array<{
      event_type: string;
      stage_order: number;
    }>;
    rejected_events?: Array<{
      event_type: string;
      stage_order: number | null;
    }>;
    n8n_webhook_url_approved?: string;
    n8n_webhook_url_rejected?: string;
    n8n_webhook_url_recovery?: string;
    use_cloud_api?: boolean;
    onboarding_template?: string;
    recovery_template?: string;
    onboarding_template_params?: string[];
    recovery_template_params?: string[];
  };
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

interface CrmStage {
  id: string;
  name: string;
  stage_order: number;
  color: string;
}

interface ProductConfig {
  name: string;
  stage_order: number;
}

interface RejectedEventConfig {
  event_type: string;
  stage_order: number | null;
  label: string;
}

const Integrations: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [formData, setFormData] = useState<Partial<Integration>>({
    name: '',
    provider: '',
    is_active: true,
    webhook_url: '',
    webhook_secret: '',
    config: {
      products: [],
    },
  });
  const [crmStages, setCrmStages] = useState<CrmStage[]>([]);
  const [newProductName, setNewProductName] = useState('');
  const [newProductStage, setNewProductStage] = useState<number>(4);
  const [newApprovedEventType, setNewApprovedEventType] = useState<string>('');
  const [newApprovedEventStage, setNewApprovedEventStage] = useState<number>(4);
  const [rejectedEventType, setRejectedEventType] = useState<string>('');
  const [rejectedEventStage, setRejectedEventStage] = useState<number | null>(4);

  const API_BASE_URL = getApiUrl();

  useEffect(() => {
    fetchData();
    fetchCrmStages();
  }, []);

  const fetchCrmStages = async () => {
    try {
      // Buscar estágios com nomes das variáveis de ambiente
      const response = await axios.get(`${API_BASE_URL}/crm/stages-with-env-names`);
      if (response.data.success && response.data.data) {
        const stages = response.data.data
          .map((stage: any) => ({
            id: stage.id,
            name: stage.name,
            stage_order: stage.stage_order,
            color: stage.color,
          }))
          .sort((a: CrmStage, b: CrmStage) => a.stage_order - b.stage_order);
        setCrmStages(stages);
        console.log('✅ Estágios do CRM carregados:', stages);
      }
    } catch (err: any) {
      console.error('Erro ao carregar estágios do CRM:', err);
      // Fallback: tentar buscar do endpoint antigo
      try {
        const fallbackResponse = await axios.get(`${API_BASE_URL}/crm/pipelines`);
        if (fallbackResponse.data.success && fallbackResponse.data.data) {
          const funilPipeline = fallbackResponse.data.data.find((p: any) => p.name === 'Funil de Vendas');
          if (funilPipeline && funilPipeline.stages) {
            const stages = funilPipeline.stages
              .map((stage: any) => ({
                id: stage.id,
                name: stage.name,
                stage_order: stage.stage_order,
                color: stage.color,
              }))
              .sort((a: CrmStage, b: CrmStage) => a.stage_order - b.stage_order);
            setCrmStages(stages);
          }
        }
      } catch (fallbackErr: any) {
        console.error('Erro ao carregar estágios do CRM (fallback):', fallbackErr);
      }
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [providersRes, integrationsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/integrations/providers/list`),
        axios.get(`${API_BASE_URL}/integrations`),
      ]);

      if (providersRes.data.success) {
        const providersData = providersRes.data.data;
        setProviders(Array.isArray(providersData) ? providersData : []);
        console.log('✅ Providers carregados:', providersData);
      } else {
        console.warn('⚠️ Resposta de providers não teve success:', providersRes.data);
      }

      if (integrationsRes.data.success) {
        const integrationsData = integrationsRes.data.data;
        setIntegrations(Array.isArray(integrationsData) ? integrationsData : []);
      } else {
        console.warn('⚠️ Resposta de integrations não teve success:', integrationsRes.data);
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar dados:', err);
      setError(err.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (provider?: Provider) => {
    if (provider) {
      setSelectedProvider(provider);
      const existingIntegration = integrations.find((i) => i.provider === provider.id);
      
      if (existingIntegration) {
        setFormData({
          ...existingIntegration,
          name: existingIntegration.name || provider.name,
          config: {
            products: existingIntegration.config?.products || [],
            approved_events: existingIntegration.config?.approved_events || [],
            rejected_events: existingIntegration.config?.rejected_events || [],
            n8n_webhook_url_approved: existingIntegration.config?.n8n_webhook_url_approved || '',
            n8n_webhook_url_rejected: existingIntegration.config?.n8n_webhook_url_rejected || '',
            n8n_webhook_url_recovery: existingIntegration.config?.n8n_webhook_url_recovery || '',
            use_cloud_api: existingIntegration.config?.use_cloud_api || false,
            onboarding_template: existingIntegration.config?.onboarding_template || '',
            recovery_template: existingIntegration.config?.recovery_template || '',
            onboarding_template_params: existingIntegration.config?.onboarding_template_params || [],
            recovery_template_params: existingIntegration.config?.recovery_template_params || [],
          },
        });
      } else {
        setFormData({
          name: provider.name,
          provider: provider.id,
          is_active: true,
          webhook_url: '',
          webhook_secret: '',
          config: {
            products: [],
            approved_events: [],
            rejected_events: [],
            use_cloud_api: false,
            onboarding_template: '',
            recovery_template: '',
            onboarding_template_params: [],
            recovery_template_params: [],
          },
        });
      }
      setNewProductName('');
      setNewProductStage(4);
      setNewApprovedEventType('');
      setNewApprovedEventStage(4);
    } else {
      setSelectedProvider(null);
      setFormData({
        name: '',
        provider: '',
        is_active: true,
        webhook_url: '',
        webhook_secret: '',
      });
    }
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedProvider(null);
    setFormData({
      name: '',
      provider: '',
      is_active: true,
      webhook_url: '',
      webhook_secret: '',
      config: {
        products: [],
        approved_events: [],
      },
    });
    setNewProductName('');
    setNewProductStage(4);
    setNewApprovedEventType('');
    setNewApprovedEventStage(4);
    setRejectedEventType('');
    setRejectedEventStage(4);
  };

  // Obter eventos disponíveis por plataforma
  const getRejectedEventsForProvider = (providerId: string): RejectedEventConfig[] => {
    const events: Record<string, RejectedEventConfig[]> = {
      hubla: [
        { event_type: 'lead.abandoned_checkout', label: 'Carrinho Abandonado', stage_order: null },
        { event_type: 'invoice.payment_failed', label: 'Pagamento Falhou', stage_order: 4 },
        { event_type: 'invoice.expired', label: 'Fatura Expirada', stage_order: 4 },
        { event_type: 'invoice.refunded', label: 'Fatura Reembolsada', stage_order: 2 },
        { event_type: 'refund_request.accepted', label: 'Reembolso Aceito', stage_order: 2 },
        { event_type: 'status.unpaid', label: 'Status: Não Pago', stage_order: 4 },
        { event_type: 'status.overdue', label: 'Status: Atrasado', stage_order: 4 },
      ],
      hotmart: [
        { event_type: 'PURCHASE_REFUNDED', label: 'Compra Reembolsada', stage_order: 2 },
        { event_type: 'PURCHASE_CANCELLED', label: 'Compra Cancelada', stage_order: 4 },
        { event_type: 'PURCHASE_EXPIRED', label: 'Compra Expirada', stage_order: 4 },
        { event_type: 'PURCHASE_CHARGEBACK', label: 'Chargeback', stage_order: 4 },
        { event_type: 'PURCHASE_PROTEST', label: 'Protesto', stage_order: 4 },
        { event_type: 'SUBSCRIPTION_CANCELLATION', label: 'Cancelamento de Assinatura', stage_order: 2 },
        { event_type: 'PURCHASE_OUT_OF_SHOPPING_CART', label: 'Abandono de Carrinho', stage_order: 1 },
      ],
      kirvano: [
        { event_type: 'SALE_REJECTED', label: 'Venda Rejeitada', stage_order: 4 },
        { event_type: 'SALE_CANCELLED', label: 'Venda Cancelada', stage_order: 4 },
        { event_type: 'SALE_REFUSED', label: 'Venda Recusada (Carrinho Abandonado)', stage_order: null },
        { event_type: 'PIX_EXPIRED', label: 'PIX Expirado (Carrinho Abandonado)', stage_order: null },
        { event_type: 'ABANDONED_CART', label: 'Carrinho Abandonado', stage_order: null },
        { event_type: 'status.CANCELED', label: 'Status: Cancelado', stage_order: 4 },
        { event_type: 'status.EXPIRED', label: 'Status: Expirado', stage_order: 4 },
      ],
      arbfast: [
        { event_type: 'charge.expired', label: 'Depósito Expirado', stage_order: 4 },
        { event_type: 'charge.cancelled', label: 'Depósito Cancelado', stage_order: 4 },
        { event_type: 'deposit.EXPIRED', label: 'Status: Expirado', stage_order: 4 },
        { event_type: 'deposit.CANCELLED', label: 'Status: Cancelado', stage_order: 4 },
      ],
      kiwify: [
        { event_type: 'order.refunded', label: 'Pedido Reembolsado', stage_order: 2 },
        { event_type: 'order.cancelled', label: 'Pedido Cancelado', stage_order: 4 },
        { event_type: 'order.expired', label: 'Pedido Expirado', stage_order: 4 },
        { event_type: 'payment.failed', label: 'Pagamento Falhou', stage_order: 4 },
      ],
      guru: [
        { event_type: 'transaction.refunded', label: 'Transação Reembolsada', stage_order: 2 },
        { event_type: 'transaction.canceled', label: 'Transação Cancelada', stage_order: 4 },
        { event_type: 'subscription.canceled', label: 'Assinatura Cancelada', stage_order: 2 },
        { event_type: 'subscription.expired', label: 'Assinatura Expirada', stage_order: 4 },
        { event_type: 'invoice.unpaid', label: 'Fatura Não Paga', stage_order: 4 },
        { event_type: 'invoice.overdue', label: 'Fatura Atrasada', stage_order: 4 },
      ],
      braip: [
        { event_type: 'transaction.refunded', label: 'Transação Reembolsada', stage_order: 2 },
        { event_type: 'transaction.cancelled', label: 'Transação Cancelada', stage_order: 4 },
        { event_type: 'transaction.expired', label: 'Transação Expirada', stage_order: 4 },
        { event_type: 'payment.failed', label: 'Pagamento Falhou', stage_order: 4 },
      ],
      perfectpay: [
        { event_type: 'payment.refunded', label: 'Pagamento Reembolsado', stage_order: 2 },
        { event_type: 'payment.cancelled', label: 'Pagamento Cancelado', stage_order: 4 },
        { event_type: 'payment.expired', label: 'Pagamento Expirado', stage_order: 4 },
        { event_type: 'payment.failed', label: 'Pagamento Falhou', stage_order: 4 },
      ],
    };
    return events[providerId] || [];
  };

  // Obter eventos aprovados disponíveis para ARBFAST
  const getApprovedEventsForArbfast = (): Array<{ event_type: string; label: string }> => {
    return [
      { event_type: 'pix_charge_created', label: 'PIX Charge Criado' },
      { event_type: 'charge.approved', label: 'Depósito Aprovado (charge.approved)' },
      { event_type: 'deposit.APPROVED', label: 'Depósito Aprovado (deposit.APPROVED)' },
      { event_type: 'charge.pending', label: 'Depósito Pendente (charge.pending)' },
      { event_type: 'deposit.PENDING', label: 'Depósito Pendente (deposit.PENDING)' },
      { event_type: 'lead', label: 'Lead' },
    ];
  };

  const handleAddProduct = () => {
    if (!newProductName.trim()) {
      setError('Nome do produto é obrigatório');
      return;
    }

    const currentProducts = formData.config?.products || [];
    const productExists = currentProducts.some(
      (p) => p.name.toLowerCase() === newProductName.toLowerCase().trim()
    );

    if (productExists) {
      setError('Este produto já está configurado');
      return;
    }

    setFormData({
      ...formData,
      config: {
        ...formData.config,
        products: [
          ...currentProducts,
          {
            name: newProductName.trim(),
            stage_order: newProductStage,
          },
        ],
      },
    });

    setNewProductName('');
    setNewProductStage(4);
    setError('');
  };

  const handleRemoveProduct = (index: number) => {
    const currentProducts = formData.config?.products || [];
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        products: currentProducts.filter((_, i) => i !== index),
      },
    });
  };

  const handleAddRejectedEvent = () => {
    if (!rejectedEventType) {
      setError('Selecione um evento');
      return;
    }

    const currentEvents = formData.config?.rejected_events || [];
    const eventExists = currentEvents.some(
      (e) => e.event_type === rejectedEventType
    );

    if (eventExists) {
      setError('Este evento já está configurado');
      return;
    }

    setFormData({
      ...formData,
      config: {
        ...formData.config,
        rejected_events: [
          ...currentEvents,
          {
            event_type: rejectedEventType,
            stage_order: rejectedEventStage,
          },
        ],
      },
    });

    setRejectedEventType('');
    setRejectedEventStage(4); // Reset para valor padrão (não null)
    setError('');
  };

  const handleRemoveRejectedEvent = (index: number) => {
    const currentEvents = formData.config?.rejected_events || [];
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        rejected_events: currentEvents.filter((_, i) => i !== index),
      },
    });
  };

  const handleAddApprovedEvent = () => {
    if (!newApprovedEventType) {
      setError('Selecione um evento');
      return;
    }

    const currentEvents = formData.config?.approved_events || [];
    const eventExists = currentEvents.some(
      (e) => e.event_type === newApprovedEventType
    );

    if (eventExists) {
      setError('Este evento já está configurado');
      return;
    }

    setFormData({
      ...formData,
      config: {
        ...formData.config,
        approved_events: [
          ...currentEvents,
          {
            event_type: newApprovedEventType,
            stage_order: newApprovedEventStage,
          },
        ],
      },
    });

    setNewApprovedEventType('');
    setNewApprovedEventStage(4);
    setError('');
  };

  const handleRemoveApprovedEvent = (index: number) => {
    const currentEvents = formData.config?.approved_events || [];
    setFormData({
      ...formData,
      config: {
        ...formData.config,
        approved_events: currentEvents.filter((_, i) => i !== index),
      },
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const payload = {
        ...formData,
        provider: selectedProvider?.id || formData.provider,
        config: {
          products: formData.config?.products || [],
          approved_events: formData.config?.approved_events || [],
          rejected_events: formData.config?.rejected_events || [],
          n8n_webhook_url_approved: formData.config?.n8n_webhook_url_approved || '',
          n8n_webhook_url_rejected: formData.config?.n8n_webhook_url_rejected || '',
          n8n_webhook_url_recovery: formData.config?.n8n_webhook_url_recovery || '',
          use_cloud_api: formData.config?.use_cloud_api || false,
          onboarding_template: formData.config?.onboarding_template || '',
          recovery_template: formData.config?.recovery_template || '',
          onboarding_template_params: formData.config?.onboarding_template_params || [],
          recovery_template_params: formData.config?.recovery_template_params || [],
        },
      };

      let response;
      if (formData.id) {
        response = await axios.put(`${API_BASE_URL}/integrations/${formData.id}`, payload);
      } else {
        response = await axios.post(`${API_BASE_URL}/integrations`, payload);
      }

      if (response.data.success) {
        setSuccess('Integração salva com sucesso!');
        await fetchData();
        setTimeout(() => {
          handleCloseDialog();
        }, 1000);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar integração');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (integration: Integration) => {
    try {
      const response = await axios.put(`${API_BASE_URL}/integrations/${integration.id}`, {
        is_active: !integration.is_active,
      });

      if (response.data.success) {
        await fetchData();
        setSuccess(`Integração ${integration.is_active ? 'desativada' : 'ativada'} com sucesso!`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar integração');
    }
  };

  const handleDelete = async (integration: Integration) => {
    if (!window.confirm(`Tem certeza que deseja deletar a integração ${integration.name}?`)) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE_URL}/integrations/${integration.id}`);

      if (response.data.success) {
        await fetchData();
        setSuccess('Integração deletada com sucesso!');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao deletar integração');
    }
  };

  const getWebhookUrl = () => {
    return `${API_BASE_URL}/integrations/webhook`;
  };

  const getProviderIcon = (providerId: string) => {
    // Mapear IDs dos providers para os caminhos das imagens
    const iconMap: Record<string, string> = {
      'hubla': '/integrations/hubla.jpeg',
      'hotmart': '/integrations/hotmart.png',
      'kirvano': '/integrations/kirvano.png',
      'arbfast': '/integrations/arbfast.png',
      'kiwify': '/integrations/kiwify.png',
      'braip': '/integrations/braip.png',
      'perfectpay': '/integrations/perfectpay.png',
      'guru': '/integrations/guru.png',
    };
    
    return iconMap[providerId.toLowerCase()] || null;
  };

  const getProviderName = (providerId: string) => {
    if (!Array.isArray(providers) || providers.length === 0) {
      return providerId;
    }
    const provider = providers.find((p) => p.id === providerId);
    return provider?.name || providerId;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress sx={{ color: 'rgba(59, 130, 246, 0.8)' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 2, md: 3 }, 
      backgroundColor: 'transparent', 
      minHeight: '100vh',
      color: 'text.primary',
    }}>
      {/* Header Sofisticado */}
      <Box className="glass-blue-card" sx={{
        mb: 3,
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{
            fontWeight: 700,
            mb: 1,
            color: '#000000',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <LinkIcon sx={{ fontSize: 32, color: 'rgba(59, 130, 246, 0.8)' }} />
            Integrações
          </Typography>
          <Typography variant="body1" sx={{ color: '#64748b' }}>
            Configure e gerencie suas integrações com plataformas externas
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            background: 'rgba(244, 67, 54, 0.2)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
            color: '#000000',
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            background: 'rgba(76, 175, 80, 0.2)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
            color: '#000000',
          }} 
          onClose={() => setSuccess('')}
        >
          {success}
        </Alert>
      )}

      {/* URL do Webhook */}
      <Card className="glass-blue-card" sx={{ 
        mb: 4,
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <LinkIcon sx={{ mr: 1, color: 'rgba(59, 130, 246, 0.8)' }} />
            <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>URL do Webhook</Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Use esta URL para configurar o webhook nas plataformas externas:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              value={getWebhookUrl()}
              InputProps={{
                readOnly: true,
              }}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(59, 130, 246, 0.08)',
                  color: '#000000',
                  '& fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={() => {
                navigator.clipboard.writeText(getWebhookUrl());
                setSuccess('URL copiada para a área de transferência!');
              }}
              sx={{
                background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                color: '#000000',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                },
              }}
            >
              Copiar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Integrações Configuradas */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ 
          color: '#000000', 
          fontWeight: 600,
          mb: 2,
        }}>
          Integrações Configuradas
        </Typography>
        <Grid container spacing={2}>
          {integrations.map((integration) => (
            <Grid item xs={12} md={6} key={integration.id}>
              <Card className="glass-blue-card" sx={{
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                },
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar 
                      src={getProviderIcon(integration.provider) || undefined}
                      sx={{ 
                        mr: 2, 
                        bgcolor: getProviderIcon(integration.provider) ? 'transparent' : 'primary.main',
                        width: 48,
                        height: 48,
                      }}
                    >
                      {!getProviderIcon(integration.provider) && '🔌'}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                        {integration.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        {getProviderName(integration.provider)}
                      </Typography>
                    </Box>
                    <Chip
                      icon={integration.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      label={integration.is_active ? 'Ativa' : 'Inativa'}
                      color={integration.is_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <Divider sx={{ my: 2, borderColor: 'rgba(59, 130, 246, 0.2)' }} />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <Tooltip title="Ver Métricas">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/integrations/${integration.provider}/metrics`)}
                        sx={{
                          color: 'rgba(59, 130, 246, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            color: 'rgba(59, 130, 246, 1)',
                          },
                        }}
                      >
                        <BarChartIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const provider = Array.isArray(providers) ? providers.find((p) => p.id === integration.provider) : undefined;
                          if (provider) {
                            handleOpenDialog(provider);
                          }
                        }}
                        sx={{
                          color: '#64748b',
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.2)',
                            color: '#000000',
                          },
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={integration.is_active ? 'Desativar' : 'Ativar'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleActive(integration)}
                        sx={{
                          color: integration.is_active ? 'rgba(76, 175, 80, 0.8)' : 'rgba(255, 255, 255, 0.5)',
                          '&:hover': {
                            backgroundColor: integration.is_active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                            color: integration.is_active ? 'rgba(76, 175, 80, 1)' : '#ffffff',
                          },
                        }}
                      >
                        {integration.is_active ? <CancelIcon /> : <CheckCircleIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Deletar">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(integration)}
                        sx={{
                          color: 'rgba(244, 67, 54, 0.8)',
                          '&:hover': {
                            backgroundColor: 'rgba(244, 67, 54, 0.2)',
                            color: 'rgba(244, 67, 54, 1)',
                          },
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Providers Disponíveis */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ 
          color: '#000000', 
          fontWeight: 600,
          mb: 2,
        }}>
          Integrações Disponíveis
        </Typography>
        {providers.length === 0 ? (
          <Alert severity="info" sx={{ 
            mt: 2,
            color: '#000000',
          }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Nenhuma integração disponível no momento. Verifique se o backend está configurado corretamente.
            </Typography>
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {providers.map((provider) => {
              const existingIntegration = integrations.find((i) => i.provider === provider.id);
              return (
                <Grid item xs={12} md={6} key={provider.id}>
                <Card className="glass-blue-card" sx={{
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3), 0 4px 8px rgba(59, 130, 246, 0.2)',
                    borderColor: 'rgba(59, 130, 246, 0.4)',
                  },
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar 
                        src={getProviderIcon(provider.id) || undefined}
                        sx={{ 
                          mr: 2, 
                          bgcolor: getProviderIcon(provider.id) ? 'transparent' : 'rgba(59, 130, 246, 0.3)',
                          fontSize: '1.5rem',
                          width: 48,
                          height: 48,
                        }}
                      >
                        {!getProviderIcon(provider.id) && (provider.icon || '🔌')}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ color: '#000000', fontWeight: 600 }}>
                          {provider.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {provider.description}
                        </Typography>
                      </Box>
                      {existingIntegration && (
                        <Chip
                          label="Configurada"
                          sx={{
                            backgroundColor: 'rgba(76, 175, 80, 0.2)',
                            color: 'rgba(76, 175, 80, 1)',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                          }}
                          size="small"
                        />
                      )}
                    </Box>
                    <Button
                      variant={existingIntegration ? 'outlined' : 'contained'}
                      fullWidth
                      startIcon={existingIntegration ? <EditIcon /> : <AddIcon />}
                      onClick={() => handleOpenDialog(provider)}
                      sx={existingIntegration ? {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        color: 'rgba(59, 130, 246, 1)',
                        '&:hover': {
                          borderColor: 'rgba(59, 130, 246, 0.8)',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        },
                      } : {
                        background: 'linear-gradient(135deg, #2563eb 0%, #a855f7 100%)',
                        color: '#000000',
                        boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)',
                          boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
                        },
                      }}
                    >
                      {existingIntegration ? 'Editar Configuração' : 'Configurar'}
                    </Button>
                  </CardContent>
                </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* Dialog de Configuração */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: '#ffffff',
            border: '2px solid #3b82f6',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(59, 130, 246, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff', 
          backgroundColor: '#3b82f6',
          borderBottom: '2px solid #2563eb',
          fontWeight: 600,
          fontSize: '1.25rem',
          py: 2,
        }}>
          {formData.id ? 'Editar Integração' : 'Configurar Integração'}
          {selectedProvider && ` - ${selectedProvider.name}`}
        </DialogTitle>
        <DialogContent sx={{ 
          background: '#ffffff',
          '&.MuiDialogContent-root': {
            padding: 3,
          },
        }}>
          {selectedProvider && (
            <>
              <TextField
                fullWidth
                label="Nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    '& fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                  },
                }}
              />

              {selectedProvider.fields.map((field) => (
                <Box key={field.key}>
                  <TextField
                    fullWidth
                    label={field.label}
                    type={field.type === 'password' ? 'password' : field.type === 'url' ? 'url' : 'text'}
                    value={formData[field.key as keyof Integration] || ''}
                    onChange={(e) =>
                      setFormData({ ...formData, [field.key]: e.target.value })
                    }
                    margin="normal"
                    required={field.required}
                    helperText={(field as any).help}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                        '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />
                </Box>
              ))}

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active || false}
                    onChange={(e) =>
                      setFormData({ ...formData, is_active: e.target.checked })
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'rgba(59, 130, 246, 1)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'rgba(59, 130, 246, 0.5)',
                      },
                    }}
                  />
                }
                label="Ativar integração"
                sx={{ 
                  mt: 2,
                  '& .MuiFormControlLabel-label': {
                    color: '#1f2937',
                  },
                }}
              />

              <Divider sx={{ 
                my: 3,
                borderColor: '#e5e7eb',
              }} />

              {/* Configuração de Webhooks */}
              <Typography variant="h6" sx={{ 
                mt: 2, 
                mb: 2,
                color: '#1f2937',
                fontWeight: 600,
              }}>
                Webhooks
              </Typography>
              <Typography variant="body2" sx={{ 
                mb: 2,
                color: '#64748b',
              }}>
                Configure as URLs dos webhooks externos para processar eventos de compra e recuperação.
              </Typography>

              <TextField
                fullWidth
                label="URL Webhook - Compra Aprovada"
                value={formData.config?.n8n_webhook_url_approved || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      n8n_webhook_url_approved: e.target.value,
                    },
                  })
                }
                margin="normal"
                placeholder="https://webhook.example.com/webhook/approved"
                helperText="URL do webhook para processar compras aprovadas"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    '& fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                  },
                }}
              />

              <TextField
                fullWidth
                label="URL Webhook - Compra Recusada"
                value={formData.config?.n8n_webhook_url_rejected || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      n8n_webhook_url_rejected: e.target.value,
                    },
                  })
                }
                margin="normal"
                placeholder="https://webhook.example.com/webhook/rejected"
                helperText="URL do webhook para processar compras recusadas/abandonadas"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    '& fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                  },
                }}
              />

              <TextField
                fullWidth
                label="URL Webhook - Chat de Recuperação"
                value={formData.config?.n8n_webhook_url_recovery || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    config: {
                      ...formData.config,
                      n8n_webhook_url_recovery: e.target.value,
                    },
                  })
                }
                margin="normal"
                placeholder="https://webhook.example.com/webhook/recovery"
                helperText="URL do webhook para processar eventos de recuperação de carrinho abandonado"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#ffffff',
                    color: '#1f2937',
                    '& fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '1px',
                    },
                    '&:hover fieldset': {
                      borderColor: '#2563eb',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3b82f6',
                      borderWidth: '2px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: '#64748b',
                  },
                  '& .MuiFormHelperText-root': {
                    color: '#64748b',
                  },
                }}
              />

              <Divider sx={{ 
                my: 3,
                borderColor: '#e5e7eb',
              }} />

              {/* Configuração WhatsApp Cloud API */}
              <Typography variant="h6" sx={{ 
                mt: 2, 
                mb: 2,
                color: '#1f2937',
                fontWeight: 600,
              }}>
                WhatsApp Cloud API
              </Typography>
              <Typography variant="body2" sx={{ 
                mb: 2,
                color: '#64748b',
              }}>
                Quando habilitado, os disparos de onboarding e recuperação são feitos diretamente pela WhatsApp Cloud API (templates), sem passar pelos webhooks de automação acima.
              </Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.config?.use_cloud_api || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          use_cloud_api: e.target.checked,
                        },
                      })
                    }
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'rgba(59, 130, 246, 1)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'rgba(59, 130, 246, 0.5)',
                      },
                    }}
                  />
                }
                label="Usar WhatsApp Cloud API para disparos"
                sx={{ 
                  mb: 2,
                  '& .MuiFormControlLabel-label': {
                    color: '#1f2937',
                  },
                }}
              />

              {formData.config?.use_cloud_api && (
                <>
                  <TextField
                    fullWidth
                    label="Template de Onboarding"
                    value={formData.config?.onboarding_template || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          onboarding_template: e.target.value,
                        },
                      })
                    }
                    margin="normal"
                    placeholder="nome_do_template"
                    helperText="Nome do template aprovado no WhatsApp Business para disparos de onboarding (compras aprovadas)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                        '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Template de Recuperação"
                    value={formData.config?.recovery_template || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          recovery_template: e.target.value,
                        },
                      })
                    }
                    margin="normal"
                    placeholder="nome_do_template"
                    helperText="Nome do template aprovado no WhatsApp Business para disparos de recuperação (carrinho abandonado, rejeitados, etc.)"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                        '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Parâmetros do Template de Onboarding (separados por vírgula)"
                    placeholder="Ex: Nome do Cliente, Valor do Produto"
                    helperText="Parâmetros que serão enviados no BODY do template. Deixe vazio se o template não precisar de parâmetros. IMPORTANTE: Templates com vídeo/imagem no header não podem receber parâmetros de texto no header - deixe vazio nesses casos."
                    value={(formData.config?.onboarding_template_params || []).join(', ')}
                    onChange={(e) => {
                      const params = e.target.value
                        .split(',')
                        .map((p) => p.trim())
                        .filter((p) => p.length > 0);
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          onboarding_template_params: params,
                        },
                      });
                    }}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                        '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Parâmetros do Template de Recuperação (separados por vírgula)"
                    placeholder="Ex: Nome do Cliente, Valor do Produto"
                    helperText="Parâmetros que serão enviados no BODY do template. Deixe vazio se o template não precisar de parâmetros. IMPORTANTE: Templates com vídeo/imagem no header não podem receber parâmetros de texto no header - deixe vazio nesses casos."
                    value={(formData.config?.recovery_template_params || []).join(', ')}
                    onChange={(e) => {
                      const params = e.target.value
                        .split(',')
                        .map((p) => p.trim())
                        .filter((p) => p.length > 0);
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          recovery_template_params: params,
                        },
                      });
                    }}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                        '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                            borderColor: '#2563eb',
                        },
                        '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#64748b',
                      },
                      '& .MuiFormHelperText-root': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />
                </>
              )}

              <Divider sx={{ 
                my: 3,
                borderColor: '#e5e7eb',
              }} />

              {/* Configuração de Produtos ou Eventos (dependendo do provider) */}
              {selectedProvider?.id === 'arbfast' ? (
                <>
                  {/* Configuração de Eventos para ARBFAST */}
                  <Typography variant="h6" sx={{ 
                    mt: 2, 
                    mb: 2,
                    color: '#1f2937',
                    fontWeight: 600,
                  }}>
                    Eventos para Vendas Aprovadas
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: '#64748b',
                  }}>
                    Configure os eventos que serão considerados como vendas aprovadas e o estágio do CRM para onde o lead será movido.
                  </Typography>

                  {/* Lista de Eventos Configurados */}
                  {formData.config?.approved_events && formData.config.approved_events.length > 0 && (
                    <Paper variant="outlined" sx={{ 
                      mb: 2, 
                      maxHeight: 200, 
                      overflow: 'auto',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                    }}>
                      <List dense>
                        {formData.config.approved_events.map((event, index) => {
                          const stage = crmStages.find((s) => s.stage_order === event.stage_order);
                          const eventInfo = getApprovedEventsForArbfast().find((e) => e.event_type === event.event_type);
                          return (
                            <ListItem key={index} sx={{
                              '&:hover': {
                                backgroundColor: '#eff6ff',
                              },
                            }}>
                              <ListItemText
                                primary={eventInfo?.label || event.event_type}
                                secondary={`Estágio: ${stage?.name || `Ordem ${event.stage_order}`}`}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: '#1f2937',
                                  },
                                  '& .MuiListItemText-secondary': {
                                    color: '#64748b',
                                  },
                                }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleRemoveApprovedEvent(index)}
                                  sx={{
                                    color: 'rgba(244, 67, 54, 0.8)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                      color: 'rgba(244, 67, 54, 1)',
                                    },
                                  }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  )}

                  {/* Formulário para Adicionar Evento */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 250, flex: 1 }}>
                      <InputLabel sx={{ color: '#64748b' }}>Evento</InputLabel>
                      <Select
                        value={newApprovedEventType}
                        label="Evento"
                        onChange={(e) => setNewApprovedEventType(e.target.value)}
                        sx={{
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#64748b',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#ffffff',
                              border: '1px solid #3b82f6',
                              maxHeight: 300,
                              width: 300,
                            },
                          },
                        }}
                      >
                        {getApprovedEventsForArbfast()
                          .filter((event) => {
                            // Filtrar eventos já configurados
                            const currentEvents = formData.config?.approved_events || [];
                            return !currentEvents.some((e) => e.event_type === event.event_type);
                          })
                          .map((event) => (
                            <MenuItem 
                              key={event.event_type} 
                              value={event.event_type}
                              sx={{
                                color: '#1f2937',
                                '&:hover': {
                                  backgroundColor: '#eff6ff',
                                },
                              }}
                            >
                              {event.label}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel sx={{ color: '#64748b' }}>Estágio do CRM</InputLabel>
                      <Select
                        value={newApprovedEventStage}
                        label="Estágio do CRM"
                        onChange={(e) => setNewApprovedEventStage(Number(e.target.value))}
                        sx={{
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#64748b',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#ffffff',
                              border: '1px solid #3b82f6',
                              maxHeight: 300,
                              width: 250,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                      >
                        {crmStages.map((stage) => (
                          <MenuItem 
                            key={stage.id} 
                            value={stage.stage_order}
                            sx={{
                              color: '#1f2937',
                              '&:hover': {
                                backgroundColor: '#eff6ff',
                              },
                            }}
                          >
                            {stage.name} (Ordem {stage.stage_order})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddApprovedEvent}
                      disabled={!newApprovedEventType}
                      sx={{
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#2563eb',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                        },
                        '&.Mui-disabled': {
                          borderColor: '#e5e7eb',
                          color: '#9ca3af',
                        },
                      }}
                    >
                      Adicionar
                    </Button>
                  </Box>
                </>
              ) : (
                <>
                  {/* Configuração de Produtos para outros providers */}
                  <Typography variant="h6" sx={{ 
                    mt: 2, 
                    mb: 2,
                    color: '#1f2937',
                    fontWeight: 600,
                  }}>
                    Produtos para Vendas Aprovadas
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 2,
                    color: '#64748b',
                  }}>
                    Configure os produtos que serão considerados como vendas aprovadas e o estágio do CRM para onde o lead será movido.
                  </Typography>

                  {/* Lista de Produtos Configurados */}
                  {formData.config?.products && formData.config.products.length > 0 && (
                    <Paper variant="outlined" sx={{ 
                      mb: 2, 
                      maxHeight: 200, 
                      overflow: 'auto',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(59, 130, 246, 0.3)',
                    }}>
                      <List dense>
                        {formData.config.products.map((product, index) => {
                          const stage = crmStages.find((s) => s.stage_order === product.stage_order);
                          return (
                            <ListItem key={index} sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              },
                            }}>
                              <ListItemText
                                primary={product.name}
                                secondary={`Estágio: ${stage?.name || `Ordem ${product.stage_order}`}`}
                                sx={{
                                  '& .MuiListItemText-primary': {
                                    color: '#000000',
                                  },
                                  '& .MuiListItemText-secondary': {
                                    color: '#64748b',
                                  },
                                }}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleRemoveProduct(index)}
                                  sx={{
                                    color: 'rgba(244, 67, 54, 0.8)',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                      color: 'rgba(244, 67, 54, 1)',
                                    },
                                  }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Paper>
                  )}

                  {/* Formulário para Adicionar Produto */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Nome do Produto"
                      value={newProductName}
                      onChange={(e) => setNewProductName(e.target.value)}
                      placeholder="Ex: Novos Ricos, AIVA, etc."
                      size="small"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleAddProduct();
                        }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          '& fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '1px',
                          },
                          '&:hover fieldset': {
                            borderColor: '#2563eb',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: '#64748b',
                        },
                      }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                      <InputLabel sx={{ color: '#64748b' }}>Estágio do CRM</InputLabel>
                      <Select
                        value={newProductStage}
                        label="Estágio do CRM"
                        onChange={(e) => setNewProductStage(Number(e.target.value))}
                        sx={{
                          backgroundColor: '#ffffff',
                          color: '#1f2937',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#2563eb',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#3b82f6',
                            borderWidth: '2px',
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#64748b',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#ffffff',
                              border: '1px solid #3b82f6',
                              maxHeight: 300,
                              width: 250,
                            },
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                          },
                        }}
                      >
                        {crmStages.map((stage) => (
                          <MenuItem 
                            key={stage.id} 
                            value={stage.stage_order}
                            sx={{
                              color: '#1f2937',
                              '&:hover': {
                                backgroundColor: '#eff6ff',
                              },
                            }}
                          >
                            {stage.name} (Ordem {stage.stage_order})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddProduct}
                      disabled={!newProductName.trim()}
                      sx={{
                        borderColor: '#3b82f6',
                        color: '#3b82f6',
                        fontWeight: 500,
                        '&:hover': {
                          borderColor: '#2563eb',
                          backgroundColor: '#eff6ff',
                          color: '#2563eb',
                        },
                        '&.Mui-disabled': {
                          borderColor: '#e5e7eb',
                          color: '#9ca3af',
                        },
                      }}
                    >
                      Adicionar
                    </Button>
                  </Box>
                </>
              )}

              <Divider sx={{ 
                my: 3,
                borderColor: '#e5e7eb',
              }} />

              {/* Configuração de Eventos de Compra Recusada/Rejeitada */}
              <Typography variant="h6" sx={{ 
                mt: 2, 
                mb: 2,
                color: '#1f2937',
                fontWeight: 600,
              }}>
                Eventos de Compra Recusada/Rejeitada
              </Typography>
              <Typography variant="body2" sx={{ 
                mb: 2,
                color: '#64748b',
              }}>
                Configure para qual estágio do CRM o lead será movido quando ocorrer cada tipo de evento de compra recusada ou rejeitada.
              </Typography>

              {/* Lista de Eventos Configurados */}
              {formData.config?.rejected_events && formData.config.rejected_events.length > 0 && (
                <Paper variant="outlined" sx={{ 
                  mb: 2, 
                  maxHeight: 200, 
                  overflow: 'auto',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderColor: 'rgba(59, 130, 246, 0.3)',
                }}>
                  <List dense>
                    {formData.config.rejected_events.map((event, index) => {
                      const stage = event.stage_order !== null ? crmStages.find((s) => s.stage_order === event.stage_order) : null;
                      const availableEvents = getRejectedEventsForProvider(selectedProvider?.id || '');
                      const eventInfo = availableEvents.find((e) => e.event_type === event.event_type);
                      return (
                        <ListItem key={index} sx={{
                          '&:hover': {
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          },
                        }}>
                          <ListItemText
                            primary={eventInfo?.label || event.event_type}
                            secondary={event.stage_order !== null 
                              ? `Estágio: ${stage?.name || `Ordem ${event.stage_order}`}`
                              : 'Carrinho Abandonado (não move para estágio)'
                            }
                            sx={{
                              '& .MuiListItemText-primary': {
                                color: '#000000',
                              },
                              '& .MuiListItemText-secondary': {
                                color: '#64748b',
                              },
                            }}
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => handleRemoveRejectedEvent(index)}
                              sx={{
                                color: 'rgba(244, 67, 54, 0.8)',
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                  color: 'rgba(244, 67, 54, 1)',
                                },
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
              )}

              {/* Formulário para Adicionar Evento Rejeitado */}
              {selectedProvider && getRejectedEventsForProvider(selectedProvider.id).length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 250, flex: 1 }}>
                    <InputLabel sx={{ color: '#64748b' }}>Evento</InputLabel>
                    <Select
                      value={rejectedEventType}
                      label="Evento"
                      onChange={(e) => {
                        setRejectedEventType(e.target.value);
                        // Definir estágio padrão baseado no evento
                        const availableEvents = getRejectedEventsForProvider(selectedProvider.id);
                        const selectedEvent = availableEvents.find((ev) => ev.event_type === e.target.value);
                        if (selectedEvent) {
                          setRejectedEventStage(selectedEvent.stage_order ?? null);
                        }
                      }}
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2563eb',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                          borderWidth: '2px',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#64748b',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                              backgroundColor: '#ffffff',
                              border: '1px solid #3b82f6',
                            maxHeight: 300,
                            width: 300,
                          },
                        },
                      }}
                    >
                      {getRejectedEventsForProvider(selectedProvider.id)
                        .filter((event) => {
                          // Filtrar eventos já configurados
                          const currentEvents = formData.config?.rejected_events || [];
                          return !currentEvents.some((e) => e.event_type === event.event_type);
                        })
                        .map((event) => (
                          <MenuItem 
                            key={event.event_type} 
                            value={event.event_type}
                            sx={{
                              color: '#1f2937',
                              '&:hover': {
                                backgroundColor: '#eff6ff',
                              },
                            }}
                          >
                            {event.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel sx={{ color: '#64748b' }}>Estágio do CRM</InputLabel>
                    <Select
                      value={rejectedEventStage ?? ''}
                      label="Estágio do CRM"
                      onChange={(e) => setRejectedEventStage(e.target.value === '' ? null : Number(e.target.value))}
                      sx={{
                        backgroundColor: '#ffffff',
                        color: '#1f2937',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2563eb',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6',
                          borderWidth: '2px',
                        },
                        '& .MuiSvgIcon-root': {
                          color: '#64748b',
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            backgroundColor: '#ffffff',
                            border: '1px solid #3b82f6',
                            maxHeight: 300,
                            width: 250,
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                      }}
                    >
                      <MenuItem 
                        value=""
                        sx={{
                          color: '#64748b',
                          fontStyle: 'italic',
                          '&:hover': {
                            backgroundColor: '#eff6ff',
                          },
                        }}
                      >
                        Carrinho Abandonado (não move para estágio)
                      </MenuItem>
                      {crmStages.map((stage) => (
                        <MenuItem 
                          key={stage.id} 
                          value={stage.stage_order}
                          sx={{
                            color: '#1f2937',
                            '&:hover': {
                              backgroundColor: '#eff6ff',
                            },
                          }}
                        >
                          {stage.name} (Ordem {stage.stage_order})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddRejectedEvent}
                    disabled={!rejectedEventType}
                    sx={{
                      borderColor: 'rgba(59, 130, 246, 0.5)',
                      color: 'rgba(59, 130, 246, 1)',
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.8)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-disabled': {
                        borderColor: 'rgba(59, 130, 246, 0.2)',
                        color: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Adicionar
                  </Button>
                </Box>
              )}

              {selectedProvider && getRejectedEventsForProvider(selectedProvider.id).length === 0 && (
                <Alert severity="info" sx={{ 
                  mt: 2,
                  backgroundColor: '#eff6ff',
                  border: '1px solid #3b82f6',
                  color: '#1f2937',
                }}>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Esta plataforma não possui eventos de compra recusada/rejeitada configurados.
                  </Typography>
                </Alert>
              )}

              <Alert severity="info" sx={{ 
                mt: 2,
                backgroundColor: '#eff6ff',
                border: '1px solid #3b82f6',
                color: '#1f2937',
              }}>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  <strong>URL do Webhook:</strong> {getWebhookUrl()}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                  Configure esta URL na plataforma {selectedProvider.name} para receber eventos.
                </Typography>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{
          borderTop: '2px solid #3b82f6',
          padding: 2,
          background: '#f8fafc',
        }}>
          <Button 
            onClick={handleCloseDialog}
            sx={{
              color: '#64748b',
              borderColor: '#e5e7eb',
              fontWeight: 500,
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: '#ffffff',
                color: '#3b82f6',
              },
            }}
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || !formData.name || !formData.provider}
            sx={{
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
              '&:hover': {
                backgroundColor: '#2563eb',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
              },
              '&:disabled': {
                backgroundColor: '#e5e7eb',
                color: '#9ca3af',
              },
              '&.Mui-disabled': {
                background: 'rgba(59, 130, 246, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {saving ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integrations;
